import PropTypes from 'prop-types';
import React, { useState, useEffect, useRef } from 'react';

import CTAButton from '../../../../CTAButton';
import OcLabel from '../../../../ComponentLibrary/input-elements/oc-label';
import { connectLocation } from '../../../../location/LocationConnectors';
import { useTRC } from '../../store';
import { QuestionWrapper, SubQuestionWrapper } from '../../styles';
import generateResultsPdf from '../WallsAboveGrade/pdfResults';
import CalculatorLayout from '../shared/CalculatorLayout';
import Footer from '../shared/Footer';
import ResultsBlock from '../shared/ResultsBlock';
import {
  WOOD_STRAP_BEHIND_GWB_RSI,
  INTERIOR_GWB_RSI_PER_MM,
  validateInputs,
  getOnCenterSpacingOptions,
  PLYWOOD_RSI_PER_MM,
  FramingText,
  insulationOptions,
  resetResults,
  roundOff,
  AIR_FILM_EXTERIOR_RSI,
} from '../shared/constants';
import { DropdownField, Section, YesNo } from '../shared/formControls';
import { smoothScrollIntoView } from '../shared/scroll';

const OSB_RSI_PER_MM = 0.0087;

// ---- Framing ranges expressed in *R* units ----
const rsiRange = {
  '2x4': { values: [12, 14] },
  '2x6': { values: [12, 14, 19, 20, 22, 24] },
  '2x8': { values: [12, 14, 19, 20, 22, 24, 28] },
  '2x10': { values: [12, 14, 19, 20, 22, 24, 28, 31] },
  '2x12': { values: [12, 14, 19, 20, 22, 24, 28, 31, 35, 40] },
};

const rsiOptions = insulationOptions(rsiRange);

const PLYWOOD_THICKNESS_OPTIONS = [
  { value: '12.5', text: '12.5 mm (1/2")' },
  { value: '15.5', text: '15.5 mm (5/8")' },
  { value: '18.5', text: '18.5 mm (3/4")' },
];

const OSB_THICKNESS_OPTIONS = [
  { value: '9.5', text: '9.5 mm (3/8")' },
  { value: '11', text: '11 mm (4/9")' },
  { value: '18.5', text: '18.5 mm (3/4")' },
];

const FRAME_RSIF = {
  '2x4': 0.757,
  '2x6': 1.19,
  '2x8': 1.56,
  '2x10': 2,
  '2x12': 2.43,
};

// Fractions of area at the frame for each spacing (from R1-1 “Frame Cavity Percentage”)
const AF_BY_SPACING = { 304: 0.145, 406: 0.13, 488: 0.115, 610: 0.1 };

const AIR_FILM_INTERIOR_RSI = 0.16;

// —— defaults for this calc’s inputs
const DEFAULTS = {
  framingType: '2x4',
  onCenterSpacing: '304',
  cavityRSI: '',
  hasSheathingMaterial: true,
  sheathingMaterialType: 'osb',
  osbThickness: '',
  plywoodThickness: '',
  hasVapourBarrier: true,
  gypsumBoardThickness: '12.7',
  hasWoodStrapping: true,
};

const FloorsOverUnheatedSpaces = ({ calcId, t }) => {
  // ---- read location from the store
  const { state, setResults: publishResults, setInputs: publishInputs } = useTRC();
  const { province, region, hdd } = state.location;
  const hasLocation = !!(province && region);
  const onCenterSpacingOptions = getOnCenterSpacingOptions(t);

  // —— hydrate local state from store (if present), else use defaults
  const savedInputs = state.inputsByCalc?.[calcId];
  const [framingType, setFramingType] = useState(savedInputs?.framingType ?? DEFAULTS.framingType);
  const [onCenterSpacing, setOnCenterSpacing] = useState(savedInputs?.onCenterSpacing ?? DEFAULTS.onCenterSpacing);
  const [cavityRSI, setCavityRSI] = useState(savedInputs?.cavityRSI ?? DEFAULTS.cavityRSI);
  const [hasVapourBarrier, setHasVapourBarrier] = useState(savedInputs?.hasVapourBarrier ?? DEFAULTS.hasVapourBarrier);

  const [hasSheathingMaterial, setHasSheathingMaterial] = useState(
    savedInputs?.hasSheathingMaterial ?? DEFAULTS.hasSheathingMaterial
  );

  const [sheathingMaterialType, setSheathingMaterialType] = useState(
    savedInputs?.sheathingMaterialType ?? DEFAULTS.sheathingMaterialType
  );

  const [osbThickness, setOsbThickness] = useState(savedInputs?.osbThickness ?? DEFAULTS.osbThickness);

  const [plywoodThickness, setPlywoodThickness] = useState(savedInputs?.plywoodThickness ?? DEFAULTS.plywoodThickness);

  const [gypsumBoardThickness, setGypsumBoardThickness] = useState(
    savedInputs?.gypsumBoardThickness ?? DEFAULTS.gypsumBoardThickness
  );

  const [hasWoodStrapping, setHasWoodStrapping] = useState(savedInputs?.hasWoodStrapping ?? DEFAULTS.hasWoodStrapping);

  const resultsRef = useRef(null);
  const [error, setError] = useState(null);

  const hasFrame = framingType !== 'none';

  useEffect(() => {
    if (!error) return;
    const timeout = setTimeout(() => setError(null), 5000);
    // eslint-disable-next-line consistent-return
    return () => clearTimeout(timeout);
  }, [error]);

  // —— whenever inputs change, publish them to the store so tab switches persist
  useEffect(() => {
    publishInputs({
      calcId,
      inputs: {
        framingType,
        onCenterSpacing,
        cavityRSI,
        hasSheathingMaterial,
        sheathingMaterialType,
        osbThickness,
        plywoodThickness,
        hasVapourBarrier,
        gypsumBoardThickness,
        hasWoodStrapping,
      },
    });
  }, [
    calcId,
    framingType,
    onCenterSpacing,
    cavityRSI,
    hasSheathingMaterial,
    sheathingMaterialType,
    osbThickness,
    plywoodThickness,
    gypsumBoardThickness,
    hasWoodStrapping,
    hasVapourBarrier,
    publishInputs,
  ]);

  const results = state.resultsByCalc?.[calcId]?.results || null;

  const rowsForTable = [
    { label: t('thermal_calculator.effective_assembly_rsi'), rsi: 'effectiveAssemblyRSI' },
    { label: t('thermal_calculator.center_of_cavity'), rsi: 'centerOfCavityRSI' },
    { label: t('thermal_calculator.installed_insulation_rsi'), rsi: 'installedInsulationRSI', roundR: true },
  ];

  // ---- calculator
  const calculate = () => {
    const sheathingRSIFromSelection = () => {
      if (!hasSheathingMaterial) return 0;
      if (sheathingMaterialType === 'osb') return OSB_RSI_PER_MM * parseFloat(osbThickness || '0');
      if (sheathingMaterialType === 'plywood') return PLYWOOD_RSI_PER_MM * parseFloat(plywoodThickness || '0');
      return 0;
    };

    const gwbT = parseFloat(gypsumBoardThickness || '0');
    const interiorGwbRSI = INTERIOR_GWB_RSI_PER_MM * gwbT;
    const strapBehindGwbRSI = hasWoodStrapping ? WOOD_STRAP_BEHIND_GWB_RSI : 0;
    const vapourBarrierRSI = hasVapourBarrier ? 0 : 0; // explicit 0 (kept in ledger, colored differently in PDF)
    const sheathingInsulationRSI = sheathingRSIFromSelection();

    // ------- Continuous sum (foundation stack) -------
    const continuousTotalRSI =
      sheathingInsulationRSI + interiorGwbRSI + strapBehindGwbRSI + AIR_FILM_INTERIOR_RSI + AIR_FILM_EXTERIOR_RSI;

    const Af = AF_BY_SPACING[onCenterSpacing] ?? 0.16;
    const Ac = Math.max(0, 1 - Af);
    const RSIf = FRAME_RSIF[framingType] ?? 0;
    const RSIc = parseFloat(cavityRSI || '0');

    const rawRSIparallel = RSIf > 0 && RSIc > 0 ? 1 / (Af / RSIf + Ac / RSIc) : RSIc || 0;
    const RSIparallel = Math.round(rawRSIparallel * 100, 10) / 100;

    // ------- Outputs -------
    const effectiveAssemblyRSI = rawRSIparallel + continuousTotalRSI;
    const centerOfCavityRSI = RSIc + continuousTotalRSI;
    const installedInsulationRSI = RSIc || 0;

    // ------- PDF/Results ledger -------

    const spacingText = onCenterSpacingOptions[onCenterSpacing];
    const nominalRsiText = rsiOptions[framingType].find(opt => String(opt.value) === String(cavityRSI))?.text || '';

    const layers = {
      continuous: [
        ...(hasSheathingMaterial
          ? [
              {
                id: 'exterior_insulation',
                label:
                  sheathingMaterialType === 'osb'
                    ? `${t(
                        'thermal_calculator.floors_over_unheated_spaces.sheathing_material'
                      )} (OSB): ${osbThickness}"`
                    : `${t(
                        'thermal_calculator.floors_over_unheated_spaces.sheathing_material'
                      )} (Plywood): ${plywoodThickness}"`,
                rsi: roundOff(sheathingInsulationRSI),
              },
            ]
          : []),
        { id: 'vb', label: t('thermal_calculator.floors_over_unheated_spaces.vapour_retarder'), rsi: vapourBarrierRSI },
        ...(hasWoodStrapping
          ? [
              {
                id: 'strap',
                label: `${t('thermal_calculator.floors_over_unheated_spaces.strapping')}¹, 19 mm (3/4")`,
                rsi: strapBehindGwbRSI,
              },
            ]
          : []),
        ...(gwbT
          ? [
              {
                id: 'gwb',
                label: `${t('thermal_calculator.floors_over_unheated_spaces.gypsum_board')}: ${gwbT} mm`,
                rsi: interiorGwbRSI,
              },
            ]
          : []),
        {
          id: 'film_int',
          label: t('thermal_calculator.floors_over_unheated_spaces.interior_air_film'),
          rsi: AIR_FILM_INTERIOR_RSI,
        },
      ],
      nonContinuous:
        // framingType !== 'none'
        hasFrame
          ? [
              {
                id: 'finish_wall_core',
                label: [
                  // eslint-disable-next-line max-len
                  `${t('thermal_calculator.floors_over_unheated_spaces.wood_studs')} (${
                    FramingText[framingType]
                  }), ${spacingText} ${t(
                    'thermal_calculator.floors_over_unheated_spaces.on_center'
                  )}, ${nominalRsiText} ${t('thermal_calculator.floors_over_unheated_spaces.nominal_insulation')}`,
                ],
                rsi: RSIparallel,
              },
            ]
          : [],
    };

    return {
      Af,
      Ac,
      RSIc,
      RSIparallel,
      interiorGwbRSI,
      strapBehindGwbRSI,
      effectiveAssemblyRSI,
      centerOfCavityRSI,
      installedInsulationRSI,
      layers,
      continuousTotalRSI: roundOff(continuousTotalRSI),
    };
  };

  const onCalculate = () => {
    if (!validateInputs({ province, region, setError, cavityRSI, t })) {
      smoothScrollIntoView(resultsRef, { block: 'start', behavior: 'smooth', offset: 0 });
      // do not change persisted inputs; just report error
      return;
    }
    // inputs are valid, clear any previous error
    setError(null);

    // perform calculation
    const res = calculate();
    publishResults({
      calcId,
      results: res,
      meta: { title: t('thermal_calculator.titles.floors_over_unheated') },
    });
    smoothScrollIntoView(resultsRef, { block: 'start', behavior: 'smooth', offset: 0 });
  };

  const onReset = () => {
    resetResults(calcId, t('thermal_calculator.titles.floors_over_unheated'), publishResults);
    // reset local inputs to defaults
    setFramingType(DEFAULTS.framingType);
    setOnCenterSpacing(DEFAULTS.onCenterSpacing);
    setCavityRSI(DEFAULTS.cavityRSI);

    setHasVapourBarrier(DEFAULTS.hasVapourBarrier);
    setGypsumBoardThickness(DEFAULTS.gypsumBoardThickness);
    setHasWoodStrapping(DEFAULTS.hasWoodStrapping);
    setError(null);
    // clear inputs in store
    publishInputs({ calcId, inputs: null });
  };

  const handleFramingTypeChange = value => {
    setFramingType(value);
    setCavityRSI('');
  };

  // ---- sidebar UI
  const sidebar = (
    <>
      <Section title={t('thermal_calculator.non_continuous_layers')}>
        <QuestionWrapper>
          <OcLabel forId="on-center-spacing" text={t('thermal_calculator.framing_type')} />
          <DropdownField
            id={`framing-type-${calcId}`}
            value={framingType}
            onChange={e => {
              handleFramingTypeChange(e.target.value);
            }}
            options={[
              { value: '2x4', text: `${t('thermal_calculator.floors_over_unheated_spaces.wood_joists')} 2x4` },
              { value: '2x6', text: `${t('thermal_calculator.floors_over_unheated_spaces.wood_joists')} 2x6` },
              { value: '2x8', text: `${t('thermal_calculator.floors_over_unheated_spaces.wood_joists')} 2x8` },
              { value: '2x10', text: `${t('thermal_calculator.floors_over_unheated_spaces.wood_joists')} 2x10` },
              { value: '2x12', text: `${t('thermal_calculator.floors_over_unheated_spaces.wood_joists')} 2x12` },
            ]}
          />
        </QuestionWrapper>
        <QuestionWrapper>
          <OcLabel
            forId="on-center-spacing"
            text={t('thermal_calculator.floors_over_unheated_spaces.framing_on_center')}
          />
          <DropdownField
            id={`on-center-spacing-${calcId}`}
            value={onCenterSpacing}
            onChange={e => {
              setOnCenterSpacing(e.target.value);
            }}
            options={Object.entries(onCenterSpacingOptions).map(([value, text]) => ({
              value,
              text,
            }))}
          />
        </QuestionWrapper>
        <QuestionWrapper>
          <OcLabel
            forId={`cavityRSI-${calcId}`}
            text={t('thermal_calculator.floors_over_unheated_spaces.cavity_nominal_rsi')}
          />
          <DropdownField
            id={`cavityRSI-${calcId}`}
            value={cavityRSI}
            onChange={e => {
              setCavityRSI(e.target.value);
            }}
            options={rsiOptions[framingType] || []}
          />
        </QuestionWrapper>
      </Section>

      <Section title={t('thermal_calculator.continuous_layers')}>
        <QuestionWrapper>
          <OcLabel text={t('thermal_calculator.floors_over_unheated_spaces.sheathing_material')} />
          <YesNo
            name={`sheathing-material-${calcId}`}
            value={hasSheathingMaterial}
            onChange={val => {
              setHasSheathingMaterial(val);
            }}
          />
        </QuestionWrapper>

        {hasSheathingMaterial && (
          <div>
            <QuestionWrapper>
              <OcLabel text={t('thermal_calculator.floors_over_unheated_spaces.select_sheathing_material')} />
              <DropdownField
                id={`sheathing-type-${calcId}`}
                value={sheathingMaterialType}
                onChange={e => setSheathingMaterialType(e.target.value)}
                options={[
                  { value: 'osb', text: t('thermal_calculator.floors_over_unheated_spaces.osb') },
                  { value: 'plywood', text: t('thermal_calculator.floors_over_unheated_spaces.plywood') },
                ]}
              />
            </QuestionWrapper>

            {sheathingMaterialType === 'osb' ? (
              <SubQuestionWrapper>
                <OcLabel text={t('thermal_calculator.floors_over_unheated_spaces.select_osb_thickness')} />
                <DropdownField
                  id={`osb-${calcId}`}
                  value={osbThickness}
                  onChange={e => setOsbThickness(e.target.value)}
                  options={OSB_THICKNESS_OPTIONS}
                />
              </SubQuestionWrapper>
            ) : (
              <SubQuestionWrapper>
                <OcLabel text={t('thermal_calculator.floors_over_unheated_spaces.select_plywood_thickness')} />
                <DropdownField
                  id={`plywood-${calcId}`}
                  value={plywoodThickness}
                  onChange={e => setPlywoodThickness(e.target.value)}
                  options={PLYWOOD_THICKNESS_OPTIONS}
                />
              </SubQuestionWrapper>
            )}
          </div>
        )}

        <QuestionWrapper>
          <OcLabel text={t('thermal_calculator.form.vapour_barrier_winter')} />
          <YesNo
            name={`vapour-barrier-${calcId}`}
            value={hasVapourBarrier}
            onChange={val => {
              setHasVapourBarrier(val);
            }}
          />
        </QuestionWrapper>

        <QuestionWrapper>
          <OcLabel
            forId={`gypsum-thickness-${calcId}`}
            text={t('thermal_calculator.floors_over_unheated_spaces.gypsum_thickness')}
          />
          <DropdownField
            id={`gypsum-thickness-${calcId}`}
            value={gypsumBoardThickness}
            onChange={e => {
              setGypsumBoardThickness(e.target.value);
            }}
            options={[
              { value: '12.7', text: t('thermal_calculator.dimensions.gypsum_12_7') },
              { value: '16', text: t('thermal_calculator.dimensions.gypsum_16') },
              { value: null, text: t('thermal_calculator.none') },
            ]}
          />
        </QuestionWrapper>

        <QuestionWrapper>
          <OcLabel text={t('thermal_calculator.floors_over_unheated_spaces.wood_strap_gypsum')} />
          <YesNo
            name={`wood-strapping-${calcId}`}
            value={hasWoodStrapping}
            onChange={val => {
              setHasWoodStrapping(val);
            }}
          />
        </QuestionWrapper>

        <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
          <CTAButton
            onClick={onCalculate}
            disabled={!hasLocation}
            text={t('thermal_calculator.calculate_r_value')}
            style={{ minWidth: '200px' }}
          />
          <CTAButton
            element="button"
            type="button"
            onClick={onReset}
            text={t('thermal_calculator.reset')}
            style={{
              minWidth: '100px',
              padding: '8px 16px',
              border: '1px solid #ccc',
              background: '#fff',
              color: '#000',
            }}
          />
        </div>
      </Section>
    </>
  );

  const handleDownload = () => {
    if (!results) return;
    generateResultsPdf({
      calcId,
      province,
      region,
      hdd,
      projectLink: 'https://insulation.owenscorning.ca/builders/calculators/thermal-project-calculator/',
      assemblyHeading: t('thermal_calculator.titles.floors_over_unheated'),
      tableTitle: t('thermal_calculator.entire_assembly'),
      tableSubtitle: t('thermal_calculator.effective_thermal_resistance'),
      rowsConfig: rowsForTable,
      results,
      t,
    });
  };

  const resultsPane = (
    <div ref={resultsRef}>
      <ResultsBlock
        calcId={calcId}
        province={province}
        region={region}
        hdd={hdd}
        error={error}
        results={results}
        rows={rowsForTable}
      />
      <Footer results={results} onDownload={handleDownload} />
    </div>
  );

  return <CalculatorLayout sidebar={sidebar} results={resultsPane} />;
};

FloorsOverUnheatedSpaces.propTypes = {
  calcId: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
};

export default connectLocation(FloorsOverUnheatedSpaces);
